// ZPL text width calculation utilities

export interface TextCalculation {
  widthInDots: number;
  heightInDots: number;
  charactersPerLine: number;
  numberOfLines: number;
}

/**
 * ZPL Font character width specifications (dots per character at 1x magnification)
 * These are based on actual Zebra font specifications
 * Font "0" is proportional and varies, but we use an average estimate
 */
export const ZPL_FONT_WIDTHS: Record<string, number> = {
  "0": 8, // Default proportional font (estimated average)
  A: 6, // Font A (6 dots per character)
  B: 9, // Font B (9 dots per character)
  C: 12, // Font C (12 dots per character)
  D: 12, // Font D (same as C)
  E: 20, // Font E
  F: 16, // Font F
  G: 48, // Font G (large)
  H: 19, // Font H
};

/**
 * Calculate accurate text dimensions for ZPL text blocks
 */
export function calculateTextDimensions(
  content: string,
  fontSize: number,
  fontFamily: string,
  maxWidth: number,
): TextCalculation {
  if (!content) {
    return {
      widthInDots: 0,
      heightInDots: fontSize,
      charactersPerLine: 0,
      numberOfLines: 0,
    };
  }

  const baseCharWidth = ZPL_FONT_WIDTHS[fontFamily] || ZPL_FONT_WIDTHS["0"];
  const charWidthInDots = baseCharWidth * (fontSize / 20); // Scale by fontSize (20 is base size)

  // Calculate how many characters fit in the max width
  const charactersPerLine = Math.floor(maxWidth / charWidthInDots);

  // Calculate number of lines needed
  const numberOfLines = Math.ceil(content.length / charactersPerLine);

  // Calculate actual width used (might be less than maxWidth)
  const actualLineLength = Math.min(content.length, charactersPerLine);
  const widthInDots = actualLineLength * charWidthInDots;

  // Calculate total height (font size * number of lines)
  const heightInDots = fontSize * numberOfLines;

  return {
    widthInDots,
    heightInDots,
    charactersPerLine,
    numberOfLines,
  };
}

/**
 * Calculate the minimum width needed to display text without wrapping
 */
export function calculateMinimumTextWidth(
  content: string,
  fontSize: number,
  fontFamily: string,
): number {
  if (!content) return 0;

  const baseCharWidth = ZPL_FONT_WIDTHS[fontFamily] || ZPL_FONT_WIDTHS["0"];
  const charWidthInDots = baseCharWidth * (fontSize / 20); // Scale by fontSize (20 is base size)

  return content.length * charWidthInDots;
}

/**
 * Estimate how much text will fit in a given width
 */
export function estimateTextFit(
  maxWidth: number,
  fontSize: number,
  fontFamily: string,
): number {
  const baseCharWidth = ZPL_FONT_WIDTHS[fontFamily] || ZPL_FONT_WIDTHS["0"];
  const charWidthInDots = baseCharWidth * (fontSize / 20); // Scale by fontSize (20 is base size)

  return Math.floor(maxWidth / charWidthInDots);
}
